import dayjs from 'dayjs';

// Regras dos formulários dinâmicos de cadastro (produtor e jurado). As regras de
// campo (visivelSe, forcarValorSe, semMascaraSe) seguem o FormularioDinamico e o
// FormularioRegrasService do backend.

const PARECE_BOOLEANO = /^(true|false|1|0)$/i;
const VERDADEIRO = /^(true|1)$/i;

/** O backend devolve booleano às vezes como true, às vezes como "true". */
export const ehVerdadeiro = (valor) => valor === true || String(valor).toLowerCase() === 'true';

export const ehLigaDesliga = (campo) => campo.tipo === 'checkbox' || campo.tipo === 'switch';

/**
 * Consentimentos (LGPD, uso de imagem, termos), reconhecidos pelo código. Depois de
 * aceitos ficam travados: revogar consentimento exige fluxo legal próprio, não pode
 * acontecer desmarcando um switch no perfil.
 */
export function ehCampoConsentimento(codigo) {
  const c = (codigo || '').toLowerCase();
  return (
    c.includes('lgpd') ||
    c.includes('aceite_imagem') ||
    c.includes('aceite_termo') ||
    c.includes('consentimento') ||
    c === 'termos'
  );
}

/** Condição `{ campo, valor }` atendida pelos valores atuais (compara também como texto). */
export function condicaoAtendida(condicao, valores) {
  if (!condicao?.campo) return false;
  const atual = valores[condicao.campo];
  return atual === condicao.valor || String(atual) === String(condicao.valor);
}

/**
 * Regra `forcarValorSe`: quando outro campo tem o valor indicado, este fica travado
 * em `valorForcado` (ex.: tipo_produtor = industrial → possui_selo = true).
 * Devolve o valor travado (texto) ou null se a regra não está ativa.
 */
export function valorForcado(campo, valores) {
  const regra = campo?.configuracao?.forcarValorSe;
  if (!regra?.campo) return null;

  const atual = String(valores[regra.campo] ?? '')
    .trim()
    .toLowerCase();
  const esperado = String(regra.valor ?? '')
    .trim()
    .toLowerCase();
  const mesmoBooleano =
    PARECE_BOOLEANO.test(atual) &&
    PARECE_BOOLEANO.test(esperado) &&
    VERDADEIRO.test(atual) === VERDADEIRO.test(esperado);
  if (atual !== esperado && !mesmoBooleano) return null;

  return String(regra.valorForcado ?? '');
}

function lerJson(texto, padrao) {
  try {
    return JSON.parse(texto);
  } catch {
    return padrao;
  }
}

/** Campos na ordem configurada, com as opções de select já lidas (`opcoes` ou `opcoesJson`). */
export function prepararCampos(campos) {
  return (campos ?? [])
    .map((campo) => ({ ...campo, opcoes: campo.opcoesJson ? lerJson(campo.opcoesJson, null) : (campo.opcoes ?? null) }))
    .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
}

function lerLista(bruto) {
  if (Array.isArray(bruto)) return bruto;
  const lista = typeof bruto === 'string' && bruto ? lerJson(bruto, []) : [];
  return Array.isArray(lista) ? lista : [];
}

/** Valores do Formik a partir do que o backend tem salvo (rascunho ou cadastro). */
export function valoresIniciais(campos, salvos, emailInicial) {
  const valores = { ...(salvos ?? {}) };
  for (const campo of campos) {
    const { codigo } = campo;
    const bruto = valores[codigo];
    const vazio = bruto === undefined || bruto === null || bruto === '';

    if (ehLigaDesliga(campo)) valores[codigo] = vazio ? false : typeof bruto === 'string' ? ehVerdadeiro(bruto) : bruto;
    else if (campo.tipo === 'date') valores[codigo] = vazio ? null : bruto;
    else if (campo.tipo === 'multiselect') valores[codigo] = lerLista(bruto);
    else if (bruto == null) valores[codigo] = '';

    // E-mail vem do login quando o cadastro ainda não tem
    if (campo.tipo === 'email' && emailInicial && !bruto) valores[codigo] = emailInicial;
    if (ehCampoConsentimento(codigo) && typeof valores[codigo] === 'string')
      valores[codigo] = ehVerdadeiro(valores[codigo]);
  }
  return valores;
}

function serializar(campo, valor) {
  if (campo.tipo === 'date') return valor ? dayjs(valor).format('YYYY-MM-DD') : '';
  if (ehLigaDesliga(campo)) return String(valor ?? false);
  if (campo.tipo === 'multiselect' && Array.isArray(valor)) return valor.length ? JSON.stringify(valor) : '';
  return valor ?? '';
}

/** Valores no formato do POST: data YYYY-MM-DD, liga/desliga "true"/"false", multiselect em JSON. */
export function serializarValores(campos, valores) {
  return Object.fromEntries(
    campos.filter((c) => c.codigo).map((campo) => [campo.codigo, serializar(campo, valores[campo.codigo])]),
  );
}

/** Campos que ocupam a linha inteira no grid do formulário. */
export const ocupaLinhaInteira = (campo) =>
  campo.codigo === 'cep' || ['checkbox', 'switch', 'multiselect', 'file'].includes(campo.tipo);

/** Algum aceite obrigatório (liga/desliga) ainda desmarcado — trava o botão de concluir. */
export const faltaAceiteObrigatorio = (campos, valores) =>
  campos.some((campo) => ehLigaDesliga(campo) && campo.obrigatorio && !ehVerdadeiro(valores[campo.codigo]));

/**
 * Rótulo no idioma atual: em pt vale o label que o cliente configurou (ex.: "Nome da
 * Cooperativa"); nos outros idiomas traduz pelo código, com o label como reserva.
 */
export function rotuloCampo(campo, t, idioma) {
  if (idioma?.startsWith('pt') && campo.label) return campo.label;
  return t(`campo.${campo.codigo}`, { defaultValue: campo.label });
}
